import type {} from '@mui/x-data-grid/themeAugmentation';
import { alpha, createTheme, darken, getContrastRatio, Shadows, Theme } from '@mui/material/styles';
import { COLOR_SCHEMES, ColorSchemeType, NEUTRALS } from './color-schemes';
import { CONTENT_MAX_WIDTH, MOTION, RADIUS, SHADOWS } from './tokens';

declare module '@mui/material/styles' {
  interface BrandPalette {
    /** Cor da entidade escurecida até 4.5:1 sobre branco — para texto, ícones e links. */
    text: string;
    /** Fundo levemente tingido (faixas, ícones, estados ativos). */
    surface: string;
    /** Fundo tingido mais forte (hover/seleção). */
    surfaceStrong: string;
    /** Borda tingida para hover de cards. */
    border: string;
    /** Tom profundo (barra utilitária e rodapé), 7:1 com branco. */
    deep: string;
  }
  interface Palette {
    brand: BrandPalette;
  }
  interface PaletteOptions {
    brand?: BrandPalette;
  }
  interface TypeBackground {
    subtle: string;
  }
}

const WHITE = '#ffffff';
const HEADING_FONT = "'Lexend', 'Source Sans 3', system-ui, sans-serif";
const BODY_FONT = "'Source Sans 3', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/** Escurece a cor em passos até atingir o contraste mínimo com o fundo. */
const ensureContrast = (color: string, background: string, minRatio: number): string => {
  let result = color;
  for (let step = 0; step < 20 && getContrastRatio(result, background) < minRatio; step++) {
    result = darken(result, 0.08);
  }
  return result;
};

/** Texto sobre a cor primária: branco quando atinge AA, senão o texto escuro dos neutros. */
const contrastTextFor = (color: string): string =>
  getContrastRatio(color, WHITE) >= 4.5 ? WHITE : NEUTRALS.text.primary;

const mobile = '@media (max-width:600px)';

/**
 * Creates a Material-UI theme based on the selected color scheme
 * @param colorScheme - The color scheme key to use
 * @returns Material-UI Theme object
 */
export const createAppTheme = (colorScheme: ColorSchemeType = 'blue'): Theme => {
  const scheme = COLOR_SCHEMES[colorScheme];
  const brandText = ensureContrast(scheme.primary, WHITE, 4.5);
  const brandDeep = ensureContrast(darken(scheme.primary, 0.45), WHITE, 7);

  const shadows = [
    'none',
    SHADOWS.sm,
    SHADOWS.sm,
    SHADOWS.md,
    SHADOWS.md,
    SHADOWS.md,
    SHADOWS.md,
    ...Array(18).fill(SHADOWS.lg),
  ] as Shadows;

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: scheme.primary,
        dark: scheme.secondary,
        light: scheme.accent,
        contrastText: contrastTextFor(scheme.primary),
      },
      secondary: {
        main: scheme.secondary,
        dark: scheme.secondary,
        light: scheme.accent,
        contrastText: contrastTextFor(scheme.secondary),
      },
      brand: {
        text: brandText,
        surface: alpha(scheme.primary, 0.06),
        surfaceStrong: alpha(scheme.primary, 0.12),
        border: alpha(scheme.primary, 0.4),
        deep: brandDeep,
      },
      background: {
        default: NEUTRALS.background.default,
        paper: NEUTRALS.background.paper,
        subtle: NEUTRALS.subtle,
      },
      text: {
        primary: NEUTRALS.text.primary,
        secondary: NEUTRALS.text.secondary,
      },
      divider: NEUTRALS.divider,
      grey: NEUTRALS.grey,
    },
    typography: {
      fontFamily: BODY_FONT,
      h1: {
        fontFamily: HEADING_FONT,
        fontSize: '2.25rem',
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        [mobile]: { fontSize: '1.75rem' },
      },
      h2: {
        fontFamily: HEADING_FONT,
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        [mobile]: { fontSize: '1.5rem' },
      },
      h3: {
        fontFamily: HEADING_FONT,
        fontSize: '1.375rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        [mobile]: { fontSize: '1.25rem' },
      },
      h4: {
        fontFamily: HEADING_FONT,
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontFamily: HEADING_FONT,
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontFamily: HEADING_FONT,
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1.0625rem',
        lineHeight: 1.65,
        [mobile]: { fontSize: '1rem' },
      },
      body2: {
        fontSize: '0.9375rem',
        lineHeight: 1.6,
      },
      caption: {
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        letterSpacing: '0.01em',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      button: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: RADIUS.control,
    },
    spacing: 8,
    shadows,
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `3px solid ${brandText}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.control,
            padding: '10px 20px',
            minHeight: 44,
            transition: `background-color ${MOTION.base}, border-color ${MOTION.base}, color ${MOTION.base}, transform ${MOTION.fast}`,
            '&:active': {
              transform: 'translateY(1px)',
            },
          },
          sizeSmall: {
            minHeight: 36,
            padding: '6px 14px',
            fontSize: '0.9375rem',
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: `background-color ${MOTION.base}, color ${MOTION.base}`,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.card,
            border: `1px solid ${NEUTRALS.divider}`,
            boxShadow: SHADOWS.sm,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.card,
            backgroundImage: 'none',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 16,
            paddingRight: 16,
            '@media (min-width:600px)': { paddingLeft: 24, paddingRight: 24 },
            '@media (min-width:900px)': { paddingLeft: 32, paddingRight: 32 },
            '&.MuiContainer-maxWidthLg, &.MuiContainer-maxWidthXl': {
              maxWidth: CONTENT_MAX_WIDTH,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.control,
            backgroundColor: NEUTRALS.background.paper,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.control,
            fontWeight: 600,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          disableGutters: true,
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.card,
            border: `1px solid ${NEUTRALS.divider}`,
            marginBottom: 12,
            overflow: 'hidden',
            '&:before': {
              display: 'none',
            },
            '&:first-of-type, &:last-of-type': {
              borderRadius: RADIUS.card,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 56,
            '&.Mui-expanded': {
              backgroundColor: NEUTRALS.subtle,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            minHeight: 48,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: NEUTRALS.grey[800],
            fontSize: '0.8125rem',
          },
          arrow: {
            color: NEUTRALS.grey[800],
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: SHADOWS.lg,
            border: `1px solid ${NEUTRALS.divider}`,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: SHADOWS.lg,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: NEUTRALS.divider,
            fontVariantNumeric: 'tabular-nums',
          },
          head: {
            backgroundColor: NEUTRALS.subtle,
            fontWeight: 600,
            color: NEUTRALS.text.primary,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontFamily: BODY_FONT,
            borderRadius: RADIUS.card,
            borderColor: NEUTRALS.divider,
            backgroundColor: NEUTRALS.background.paper,
            fontVariantNumeric: 'tabular-nums',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: NEUTRALS.subtle,
              borderBottomColor: NEUTRALS.divider,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              borderBottomColor: NEUTRALS.divider,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(scheme.primary, 0.04),
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
              outline: `2px solid ${brandText}`,
              outlineOffset: -2,
            },
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.9375rem',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
      },
    },
  });
};
